"use client";

/**
 * `AgentChat.Context` — shadcn's Context pattern
 * (https://ui.shadcn.com/docs/ai-elements/base/context): a hover card over the
 * share of the model's context window the thread has used.
 *
 * `usedTokens` is the thread's own accounting. The window size is not on the
 * wire, so it comes from tokenlens for the model `AgentChat.Settings` reports
 * (throws without one).
 */
import { Button } from "@repo/ui/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@repo/ui/components/ui/hover-card";
import { Progress } from "@repo/ui/components/ui/progress";
import { getContextWindow } from "tokenlens";
import { useAgentChatSettings } from "./settings";

const DEFAULT_CONTEXT_MAX = 100_000;

export interface AgentChatContextWindowProps {
  usedTokens: number;
}

function resolveContextWindow(model: string): number {
  const match = model.match(/^gateway\/(.+)$/);
  const tokenlensModelId = match ? match[1] : model;
  try {
    return getContextWindow(tokenlensModelId).totalMax ?? DEFAULT_CONTEXT_MAX;
  } catch {
    return DEFAULT_CONTEXT_MAX;
  }
}

function formatTokens(value: number) {
  if (value < 1000) {
    return String(value);
  }
  return `${(value / 1000).toFixed(1).replace(/\.0$/, "")}k`;
}

export function AgentChatContextWindow({ usedTokens }: AgentChatContextWindowProps) {
  const { model } = useAgentChatSettings();
  const limit = resolveContextWindow(model);
  const percent = limit > 0 ? Math.round((usedTokens / limit) * 100) : 0;

  return (
    <HoverCard>
      <HoverCardTrigger delay={10} closeDelay={100} render={<Button variant="outline" size="sm" />}>
        {percent}% context
      </HoverCardTrigger>
      <HoverCardContent className="flex w-64 flex-col gap-3">
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="font-medium">Context</span>
          <span className="text-muted-foreground">
            {formatTokens(usedTokens)} / {formatTokens(limit)}
          </span>
        </div>
        <Progress value={percent} aria-label="Context used" />
      </HoverCardContent>
    </HoverCard>
  );
}
